import { useCallback, useEffect, useRef } from "react";
import { Platform } from "react-native";
import { Audio as ExpoAudio } from "expo-av";
import * as FileSystem from "expo-file-system";
import { Buffer } from "buffer";

export type StreamAudioChunk = {
  seq: number;
  audioBase64: string;
  text?: string;
  isFinalChunk?: boolean;
};

const PCM_SAMPLE_RATE = 44100;
const PCM_CHANNELS = 1;
const PCM_BITS_PER_SAMPLE = 16;
const WAV_HEADER_SIZE_BYTES = 44;

class StreamingPcmPlayer {
  private readonly ctx: AudioContext;
  private readonly sampleRate: number;
  private nextPlayTime: number;
  private readonly activeSources = new Set<AudioBufferSourceNode>();

  constructor(sampleRate = PCM_SAMPLE_RATE) {
    this.sampleRate = sampleRate;
    this.ctx = new AudioContext({ sampleRate });
    this.nextPlayTime = this.ctx.currentTime + 0.05;
  }

  async resume() {
    if (this.ctx.state !== "running") {
      await this.ctx.resume();
    }
  }

  enqueuePcm16(base64: string) {
    const pcm = this.decodePcm16(base64);
    if (pcm.length === 0) {
      return;
    }

    const buffer = this.ctx.createBuffer(1, pcm.length, this.sampleRate);
    buffer.copyToChannel(pcm, 0);

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.ctx.destination);
    source.onended = () => {
      this.activeSources.delete(source);
    };
    this.activeSources.add(source);

    const now = this.ctx.currentTime;
    this.nextPlayTime = Math.max(this.nextPlayTime, now + 0.03);
    source.start(this.nextPlayTime);
    this.nextPlayTime += buffer.duration;
  }

  reset() {
    for (const source of this.activeSources) {
      source.stop();
    }
    this.activeSources.clear();
    this.nextPlayTime = this.ctx.currentTime + 0.05;
  }

  async destroy() {
    this.reset();
    await this.ctx.close();
  }

  private decodePcm16(base64: string) {
    const bytes = Buffer.from(base64, "base64");
    const pcm = new Float32Array(Math.floor(bytes.length / 2));

    for (let i = 0; i < pcm.length; i += 1) {
      const lo = bytes[i * 2];
      const hi = bytes[i * 2 + 1];
      let sample = (hi << 8) | lo;
      if (sample >= 0x8000) {
        sample -= 0x10000;
      }
      pcm[i] = Math.max(-1, Math.min(1, sample / 32768));
    }

    return pcm;
  }
}

const pcmToWavBase64 = (
  pcmBase64: string,
  sampleRate = PCM_SAMPLE_RATE,
  channels = PCM_CHANNELS,
  bitsPerSample = PCM_BITS_PER_SAMPLE
) => {
  const pcmBuffer = Buffer.from(pcmBase64, "base64");
  const dataSize = pcmBuffer.length;
  const byteRate = sampleRate * channels * (bitsPerSample / 8);
  const blockAlign = channels * (bitsPerSample / 8);
  const wavBuffer = Buffer.alloc(WAV_HEADER_SIZE_BYTES + dataSize);

  wavBuffer.write("RIFF", 0, "ascii");
  wavBuffer.writeUInt32LE(36 + dataSize, 4);
  wavBuffer.write("WAVE", 8, "ascii");
  wavBuffer.write("fmt ", 12, "ascii");
  wavBuffer.writeUInt32LE(16, 16);
  wavBuffer.writeUInt16LE(1, 20);
  wavBuffer.writeUInt16LE(channels, 22);
  wavBuffer.writeUInt32LE(sampleRate, 24);
  wavBuffer.writeUInt32LE(byteRate, 28);
  wavBuffer.writeUInt16LE(blockAlign, 32);
  wavBuffer.writeUInt16LE(bitsPerSample, 34);
  wavBuffer.write("data", 36, "ascii");
  wavBuffer.writeUInt32LE(dataSize, 40);
  pcmBuffer.copy(wavBuffer, WAV_HEADER_SIZE_BYTES);
  return wavBuffer.toString("base64");
};

export function useAITutorAudioPlayback(isAudioEnabled: boolean, resourceId: string) {
  const pcmPlayerRef = useRef<StreamingPcmPlayer | null>(null);
  const lastWebSeqRef = useRef(0);
  const isAudioEnabledRef = useRef(isAudioEnabled);

  // Native fallback state.
  const nativeQueueRef = useRef<StreamAudioChunk[]>([]);
  const nativePlayingRef = useRef(false);
  const nativePlayedSeqRef = useRef(0);
  const soundObjectRef = useRef<ExpoAudio.Sound>(new ExpoAudio.Sound());

  useEffect(() => {
    if (Platform.OS !== "web") {
      return undefined;
    }
    pcmPlayerRef.current = new StreamingPcmPlayer(PCM_SAMPLE_RATE);
    return () => {
      const player = pcmPlayerRef.current;
      pcmPlayerRef.current = null;
      if (player) {
        void player.destroy();
      }
    };
  }, []);

  const resetAudioPlayback = useCallback(async () => {
    if (Platform.OS === "web") {
      pcmPlayerRef.current?.reset();
      lastWebSeqRef.current = 0;
      return;
    }

    await soundObjectRef.current.stopAsync().catch(() => undefined);
    await soundObjectRef.current.unloadAsync().catch(() => undefined);
    nativeQueueRef.current = [];
    nativePlayedSeqRef.current = 0;
    nativePlayingRef.current = false;
  }, []);

  useEffect(() => {
    void resetAudioPlayback();
  }, [resourceId, resetAudioPlayback]);

  useEffect(() => {
    isAudioEnabledRef.current = isAudioEnabled;
    if (!isAudioEnabled) {
      void resetAudioPlayback();
    }
  }, [isAudioEnabled, resetAudioPlayback]);

  const playNextNativeChunk = useCallback(async () => {
    if (nativePlayingRef.current || !isAudioEnabledRef.current || Platform.OS === "web") {
      return;
    }

    const nextIndex = nativeQueueRef.current.findIndex((chunk) => chunk.seq > nativePlayedSeqRef.current);
    if (nextIndex === -1) {
      return;
    }
    const [chunk] = nativeQueueRef.current.splice(nextIndex, 1);
    if (!chunk?.audioBase64?.trim()) {
      return;
    }

    nativePlayingRef.current = true;
    try {
      const wavBase64 = pcmToWavBase64(chunk.audioBase64);
      const uri = `${FileSystem.documentDirectory}aitutor-stream-${chunk.seq}.wav`;
      await FileSystem.writeAsStringAsync(uri, wavBase64, { encoding: FileSystem.EncodingType.Base64 });
      await soundObjectRef.current.unloadAsync().catch(() => undefined);
      await soundObjectRef.current.loadAsync({ uri });
      await soundObjectRef.current.playAsync();
      await new Promise<void>((resolve) => {
        soundObjectRef.current.setOnPlaybackStatusUpdate((status) => {
          if (status.isLoaded && status.didJustFinish) {
            resolve();
          }
        });
      });
      nativePlayedSeqRef.current = Math.max(nativePlayedSeqRef.current, chunk.seq);
    } finally {
      nativePlayingRef.current = false;
      if (isAudioEnabledRef.current) {
        void playNextNativeChunk();
      }
    }
  }, []);

  const handleAudioStreamChunk = useCallback(
    (payload: StreamAudioChunk) => {
      if (!isAudioEnabledRef.current || !payload.audioBase64?.trim()) {
        return;
      }

      if (Platform.OS === "web") {
        if (payload.seq <= lastWebSeqRef.current) {
          return;
        }
        lastWebSeqRef.current = payload.seq;
        void (async () => {
          if (!isAudioEnabledRef.current) {
            return;
          }
          await pcmPlayerRef.current?.resume();
          if (!isAudioEnabledRef.current) {
            return;
          }
          pcmPlayerRef.current?.enqueuePcm16(payload.audioBase64);
        })();
        return;
      }

      const exists = nativeQueueRef.current.some((chunk) => chunk.seq === payload.seq);
      if (exists) {
        return;
      }
      nativeQueueRef.current.push(payload);
      nativeQueueRef.current.sort((a, b) => a.seq - b.seq);
      void playNextNativeChunk();
    },
    [playNextNativeChunk]
  );

  const notifyStreamStarted = useCallback(() => {
    if (Platform.OS !== "web") {
      return;
    }
    void pcmPlayerRef.current?.resume();
  }, []);

  const notifyStreamCompleted = useCallback(() => {
    // No-op on web: playback is continuously scheduled in AudioContext time.
  }, []);

  return { resetAudioPlayback, handleAudioStreamChunk, notifyStreamStarted, notifyStreamCompleted };
}
